// Locale rules for the `$text/` keyed-text store in `global_common` (#4210).
//
// These helpers are deliberately free of Angular DI and of the PouchDB runtime
// library, so they run under plain node in unit tests. (The one `@cf-shared`
// import below is a dependency-free error-shape helper, which is why that still
// holds.) Same split `exercise-diff.ts` uses to keep pure logic out of the
// injectable service. `TextDbService` is a thin adapter over them.
//
// See design-docs/global-common-localization.md.

import type { Locales } from '../../gensrc/i18n/i18n-types';
import { BOOT_LOCALE } from '../i18n/ui-language';
import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import {
  buildExerciseTextMap,
  ENGLISH_TEXT_SEGMENT,
  exerciseTextDocId,
  exerciseTextDocIdPrefix,
} from '@cf-shared/data-model/exercise-text-keys';
import type { ExerciseTextField, ExerciseTextMap } from '@cf-shared/data-model/exercise-text-keys';

export { ENGLISH_TEXT_SEGMENT };


/**
 * Runtime UI language -> `$text/` locale segment. The segments are
 * region-tagged because the English documents already were (`en-us`), so the
 * other languages mirror that shape rather than using the bare runtime code.
 *
 * `satisfies Record<Locales, ...>` keeps the table TOTAL: adding a language to
 * `supported-locales.json` without giving it a segment is a compile error, not
 * a silent fallback to English. `as const` keeps the literal types so
 * `TextLocaleSegment` below is a union of the real segments.
 */
const TEXT_LOCALE_SEGMENTS = {
  en: ENGLISH_TEXT_SEGMENT,
  es: 'es-es',
  nl: 'nl-nl',
  fr: 'fr-fr',
} as const satisfies Record<Locales, string>;

/**
 * One of the four real locale segments. Segments are a distinct type from the
 * runtime locale codes on purpose: passing `'es'` where `'es-es'` belongs would
 * match no document and silently serve English everywhere, which is exactly the
 * failure this module exists to prevent. Now it does not compile.
 */
export type TextLocaleSegment = typeof TEXT_LOCALE_SEGMENTS[Locales];

/** The `$text/` locale segment for one UI language. */
export function textSegmentForLocale(locale: Locales): TextLocaleSegment {
  return TEXT_LOCALE_SEGMENTS[locale];
}

/**
 * The segment for the language this app session is showing.
 *
 * It is derived from `BOOT_LOCALE` — the locale captured once at module load,
 * which is what `LL` is actually bound to — and NOT from a fresh read of the
 * device key. Those two can differ: the first-run language default
 * (`location.service.ts`) writes `cs_ui_language` without reloading straight
 * away, so a re-read would return the new language while the rest of the UI is
 * still rendering the old one. Deriving from `BOOT_LOCALE` keeps help text and
 * interface text in the same language by construction. Changing the language in
 * Settings reloads the app, which is what makes a session-fixed value correct.
 */
export const ACTIVE_TEXT_SEGMENT: TextLocaleSegment = textSegmentForLocale(BOOT_LOCALE);

// #4284 — the per-locale replication filter for `global_common`.
//
// `global_common` used to replicate in FULL to every device, so a user
// downloaded all four languages of `$text/`. These helpers build the Mango
// selector that keeps only English (the always-present fallback) plus the user's
// active language, while everything that is NOT under `$text/` (config, curated
// exercise records, slots, design docs) keeps replicating exactly as before.
//
// The selector is server-side (CouchDB evaluates `_changes?filter=_selector`), so
// the other languages never reach the device. It lives here, in the pure module,
// so it is unit-tested without the database layer — the tripwire test evaluates it
// with the same `pouchdb-selector-core` PouchDB replication uses.

/**
 * A subset of a PouchDB / CouchDB Mango selector — enough to type the ones we
 * build here. `replicate.from`'s `selector` option accepts this shape.
 */
export type MangoSelector = Record<string, unknown>;

/**
 * The `$text/<segment>/` id prefix for one language. It covers EVERY keyed-text
 * document of that language — both `@help:` and `@exercise:` — which is why it is
 * the unit the replication filter works in.
 */
export function textSegmentDocIdPrefix(segment: TextLocaleSegment): string {
  return `$text/${segment}/`;
}

// The `$text/` namespace prefix. Every business document (config, exercise, slot,
// design) sorts OUTSIDE this namespace: `$`(0x24) is below `_design`(0x5F) and
// the lowercase-letter prefixes, so those ids are all `>= '$text/' + HIGH_ID_CHAR`
// and always included.
const TEXT_NAMESPACE_PREFIX = '$text/';

// The character that sorts after every printable one, so `[prefix, prefix +
// HIGH_ID_CHAR)` is the whole id range under `prefix`. Same convention as the
// `log/￰` range already used by the initial-sync selector in
// database-wrapper.ts, which is the production precedent this relies on.
const HIGH_ID_CHAR = '￰';

/** The id-range clause matching every document of ONE locale segment. */
function segmentRangeClause(segment: TextLocaleSegment): MangoSelector {
  const prefix = textSegmentDocIdPrefix(segment);
  return { _id: { $gte: prefix, $lt: prefix + HIGH_ID_CHAR } };
}

/**
 * Whether one document should replicate to a device showing `activeSegment`.
 *
 * This is the pure SPEC the Mango selector below must match (a tripwire test pins
 * that they agree). The rule: the server-only design docs and the device-local
 * cache never replicate; anything that is not a `$text/` document always does;
 * and a `$text/` document replicates only for English (the fallback) or the
 * active language.
 *
 * `excludedDocIds` is passed in (not owned here) so this stays free of the
 * database layer and cannot drift from the list `replicationFilter` uses — the
 * caller supplies the same `['_design/view', '_design/probe', ...device-local]`.
 */
export function isGlobalCommonDocIncludedForLocale(
  docId: string,
  activeSegment: TextLocaleSegment,
  excludedDocIds: readonly string[],
): boolean {
  if (excludedDocIds.includes(docId)) return false;
  if (!docId.startsWith(TEXT_NAMESPACE_PREFIX)) return true;
  return docId.startsWith(textSegmentDocIdPrefix(ENGLISH_TEXT_SEGMENT))
    || docId.startsWith(textSegmentDocIdPrefix(activeSegment));
}

/**
 * A clause every document matches, so it changes WHICH documents the selector
 * picks not at all. It exists only because PouchDB builds the replication
 * checkpoint's id from the selector: adding it gave every device a fresh
 * `global_common` checkpoint once (#5688). Before that fix, a first download cut
 * by a network error could leave a device with documents missing and a
 * checkpoint already past them, and nothing would ever fetch them. The one walk
 * of the changes feed this costs re-offers every document; ones the device
 * already holds at the same revision are not downloaded again.
 *
 * Do not remove it: that changes the selector too, and resets every device's
 * checkpoint a second time. See design-docs/global-common-localization.md,
 * "Checkpoint behaviour".
 */
const GLOBAL_COMMON_CHECKPOINT_RESET_CLAUSE: MangoSelector = { _id: { $gt: '' } };

/**
 * The Mango selector for a normal user's `global_common` pull: English + the
 * active language of `$text/`, plus everything outside the `$text/` namespace.
 *
 * Passed straight to `replicate.from({ selector })` for BOTH initial and live
 * sync, so the two cannot drift. It fully subsumes `replicationFilter`: the
 * `$nin` clause carries the same design-doc / device-local exclusions, so the JS
 * filter is not (and must not be) passed alongside it.
 *
 * It deliberately does NOT filter out `_deleted` tombstones: this replaces a JS
 * filter that let deletions through, so a server-side delete of an in-locale
 * document must still propagate and remove the local copy. The change is purely
 * the locale dimension.
 */
export function buildGlobalCommonLocaleSelector(
  activeSegment: TextLocaleSegment,
  excludedDocIds: readonly string[],
): MangoSelector {
  const localeOr: MangoSelector[] = [
    // everything before the `$text/` namespace (empty in practice) ...
    { _id: { $lt: TEXT_NAMESPACE_PREFIX } },
    // ... and everything after it — config/exercise/slot/_design all sort here
    { _id: { $gte: TEXT_NAMESPACE_PREFIX + HIGH_ID_CHAR } },
    // English, the always-present fallback
    segmentRangeClause(ENGLISH_TEXT_SEGMENT),
  ];
  // if the active language is not English, add its id range too (English is
  // already covered above)
  if (activeSegment !== ENGLISH_TEXT_SEGMENT) {
    localeOr.push(segmentRangeClause(activeSegment));
  }
  return {
    $and: [
      { _id: { $nin: [...excludedDocIds] } },
      { $or: localeOr },
      GLOBAL_COMMON_CHECKPOINT_RESET_CLAUSE,
    ],
  };
}

/**
 * The Mango selector for a one-shot pull of a SINGLE language's `$text/`
 * documents. Used to fetch a newly chosen language before a language switch
 * reload, so the app never comes up in the new language with its documents
 * missing (see `common-db.service.ts`). Like the full selector above, it does not
 * filter tombstones, so a deletion in that language propagates too.
 */
export function localeTextRangeSelector(segment: TextLocaleSegment): MangoSelector {
  return segmentRangeClause(segment);
}

const HELP_KEY_PREFIX = '@help:';

// matches a help document in ANY locale segment, capturing the help id. Built
// from HELP_KEY_PREFIX so the literal cannot drift between the two.
const HELP_DOC_ID_PATTERN = new RegExp(`^\\$text/[^/]+/${HELP_KEY_PREFIX}(.+)$`);

/** Id prefix used to list every help document of one locale. */
export function helpDocIdPrefixForSegment(segment: TextLocaleSegment): string {
  return `$text/${segment}/${HELP_KEY_PREFIX}`;
}

/** Full document id for one help text in one locale. */
export function helpDocIdForSegment(helpId: string, segment: TextLocaleSegment): string {
  return helpDocIdPrefixForSegment(segment) + helpId;
}

/**
 * The help id inside a `$text/<any-locale>/@help:<helpId>` document id, or
 * `undefined` when the id is not a help document. Callers that iterate over
 * whatever the database returned should prefer this over the throwing form, so
 * one malformed document cannot take down a whole list.
 */
export function tryHelpIdFromDocId(docId: string): string | undefined {
  const match = docId.match(HELP_DOC_ID_PATTERN);
  return match ? match[1] : undefined;
}

/** As `tryHelpIdFromDocId`, but throws when the id is not a help document. */
export function helpIdFromDocId(docId: string): string {
  const helpId = tryHelpIdFromDocId(docId);
  if (helpId === undefined) {
    throw Error(`Invalid help id while parsing ${docId}`);
  }
  return helpId;
}

// ── Curated-exercise text keys ──────────────────────────────────────
//
// The key grammar and the resolution rule live in `@cf-shared` because the
// server's workout-plan-scanner catalog and the `tools/` GDPR export read the
// same documents (#4285). They are re-exported here so every existing client
// caller keeps importing them from `text-locale`, which is where the rest of
// this store's rules live.

export {
  EXERCISE_TEXT_FIELDS,
  shortExerciseId,
  exerciseTextKey,
  tryExerciseTextKeyFromDocId,
  buildExerciseTextMap,
  hydrateExerciseText,
  withoutInlineExerciseText,
  curatedExerciseIdsWithoutName,
} from '@cf-shared/data-model/exercise-text-keys';
export type {
  ExerciseTextField,
  ExerciseTextMap,
  LocalizableExercise,
} from '@cf-shared/data-model/exercise-text-keys';

/**
 * Id prefix used to list every curated-exercise text document of one locale.
 *
 * A thin wrapper over the shared helper, which takes a plain string: this one
 * narrows the segment to a real segment at compile time, which the shared
 * module cannot do because the segment union is generated from the client's
 * locale list.
 */
export function exerciseTextDocIdPrefixForSegment(segment: TextLocaleSegment): string {
  return exerciseTextDocIdPrefix(segment);
}

/** Full document id for one field of one curated exercise, in one locale. */
export function exerciseTextDocIdForSegment(
  exerciseDocId: string,
  field: ExerciseTextField,
  segment: TextLocaleSegment,
): string {
  return exerciseTextDocId(exerciseDocId, field, segment);
}

/**
 * How many curated-exercise documents each side of the read returned. Same
 * purpose as `HelpListReadCounts`: an id-prefix query returns an EMPTY LIST
 * rather than failing when the database is unavailable, so only the counts can
 * tell "this language has nothing" from "the read did not work".
 */
export interface ExerciseTextReadCounts {
  englishCount: number;
  localizedCount: number;
}

/** The curated-exercise text of the two locales a session can need. */
export interface ExerciseTextMaps {
  /** the language being shown; the same object as `english` for an English session */
  localized: ExerciseTextMap;
  /** English, the per-field fallback and the authority on which fields exist */
  english: ExerciseTextMap;
}

/**
 * Which language an exercise read resolves its text in.
 *
 * This is deliberately a two-value choice rather than a `TextLocaleSegment`. A
 * read only ever wants one of two things — the language being shown, or the
 * authoring copy — so a parameter that accepted all four segments would
 * silently serve French text to a caller that asked for Spanish. Naming the two
 * real intentions makes that impossible to write.
 */
export enum ExerciseTextLanguage {
  /** the language the app is showing — right for everything a user reads */
  Active = 'Active',
  /**
   * The authoring copy: the exercise's ENGLISH text, never a translation.
   *
   * English is the authoring language of the curated catalog — it is what the
   * admin editor writes and what every translation is made from — so reading it
   * back is what makes "load, edit, save" round-trip exactly. Three kinds of
   * caller need it, and for the same underlying reason: what they read becomes
   * what they WRITE.
   *
   *  - the admin surfaces, which save the exercise back;
   *  - forking a curated exercise, which copies the text into the user's own
   *    custom exercise — those are never translated, so a fork must not smuggle
   *    a translation into them;
   *  - the fork's update check and diff, which compare a stored fork against its
   *    source field by field. Comparing against translated text would report
   *    every field as changed the moment the user switched language, and "pull
   *    update" would rewrite the fork into that language.
   *
   * Before #4285 this was served by skipping the `$text/` store altogether and
   * handing back the exercise document's own inline fields. That worked only
   * while those fields were still there; #4285 deletes them, so the English
   * `$text/` documents are now where the authoring copy is read from, with the
   * inline field kept as the fallback — which is what still serves a CUSTOM
   * exercise, since it has no documents in this store at all.
   */
  Authoring = 'Authoring',
}

/**
 * Which two maps `hydrateExerciseText` should be given for a read language.
 *
 * `Authoring` gets English on BOTH sides. That is not a trick: passing the same
 * map twice makes the first resolution step a no-op, so the rule collapses to
 * "English, then the inline field" — the authoring copy, with no way for a
 * translation to be read back.
 */
export function pickExerciseTextMaps(
  language: ExerciseTextLanguage,
  maps: ExerciseTextMaps,
): { localized: ExerciseTextMap; english: ExerciseTextMap } {
  if (language === ExerciseTextLanguage.Authoring) {
    return { localized: maps.english, english: maps.english };
  }
  return { localized: maps.localized, english: maps.english };
}

/**
 * Read the whole curated-exercise catalog in one language, plus English as the
 * fallback. An English session does ONE query; everyone else does two.
 *
 * `readListForPrefixAsync` runs one id-prefix query, so the caller owns the
 * database and its watcher and this stays testable without either — the same
 * split `readHelpListWithEnglishFallbackAsync` uses.
 */
export async function readExerciseTextMapsAsync(
  readListForPrefixAsync: (prefix: string) => Promise<{ _id: string; text?: string }[]>,
  segment: TextLocaleSegment = ACTIVE_TEXT_SEGMENT,
  malformedDocIds?: string[],
  counts?: ExerciseTextReadCounts,
): Promise<ExerciseTextMaps> {
  const readMapAsync = async (forSegment: TextLocaleSegment) => buildExerciseTextMap(
    await readListForPrefixAsync(exerciseTextDocIdPrefixForSegment(forSegment)), malformedDocIds);

  const english = await readMapAsync(ENGLISH_TEXT_SEGMENT);
  // an English session has nothing to fall back FROM, so it needs one query
  if (segment === ENGLISH_TEXT_SEGMENT) {
    if (counts) {
      counts.englishCount = english.size;
      counts.localizedCount = english.size;
    }
    return { localized: english, english };
  }
  const localized = await readMapAsync(segment);
  if (counts) {
    counts.englishCount = english.size;
    counts.localizedCount = localized.size;
  }
  return { localized, english };
}

/**
 * Whether a curated-exercise read produced something worth keeping.
 *
 * Both maps must have content. After #4210 Phase C every curated exercise has
 * documents in every language, so an empty map means the read did not work — an
 * id-prefix query answers with an EMPTY LIST rather than an error while the
 * database is recovering — and not that the data is genuinely empty.
 */
export function isExerciseTextUsable(maps: ExerciseTextMaps): boolean {
  return maps.english.size > 0 && maps.localized.size > 0;
}

/** How long the two "wait before reading again" rules wait. */
export interface ExerciseTextRereadIntervals {
  /** minimum gap before an UNUSABLE read is tried again */
  retryIntervalMs: number;
  /** minimum gap before a STALE cache is refreshed, so arriving batches coalesce */
  staleCoalesceMs: number;
}

/** What the caller knows about its cached curated-exercise read. */
export interface ExerciseTextCacheState {
  /** a read has been started at least once (its promise may still be pending) */
  hasCachedRead: boolean;
  /** that read has not resolved yet */
  isReadInFlight: boolean;
  /** an `@exercise:` document changed since the cached read was started */
  isStale: boolean;
  /** the last COMPLETED read produced something usable */
  lastReadWasUsable: boolean;
  /** how long ago the last read COMPLETED */
  msSinceLastRead: number;
}

/**
 * Whether the curated-exercise catalog should be read again now.
 *
 * The rule has to satisfy three needs. A read that came back unusable must be
 * RETRIED, or a transient failure would strand a non-English user on English for
 * the whole session. It must not be retried on every call:
 * `getGlobalExerciseByIdAsync` runs in loops, so an unusable state that lasts —
 * a device still syncing, or a language whose documents have not been uploaded
 * yet — would turn every exercise load into two prefix scans, forever, with
 * nothing able to end it. And a read that is still ON ITS WAY must not be
 * started a second time: `msSinceLastRead` is measured from the last COMPLETED
 * read, so during a slow first read the state would otherwise look exactly like
 * "the last one was unusable" and a device mid-sync — the very case the throttle
 * exists for — would stack a fresh catalog scan every interval.
 *
 * So an unusable result is kept and re-read no more often than
 * `retryIntervalMs`. The `DocWatcher` remains the fast path: the moment an
 * `@exercise:` document actually arrives it marks the cache STALE, and a stale
 * cache is refreshed on its own much shorter wait, `staleCoalesceMs` — short
 * because a real change should show up almost at once, but not zero because
 * documents arrive in batches.
 */
export function shouldRereadExerciseText(
  state: ExerciseTextCacheState,
  intervals: ExerciseTextRereadIntervals,
): boolean {
  // a read already on its way — every caller awaits that same promise, and a
  // second one would just duplicate it
  if (state.isReadInFlight) {
    return false;
  }
  if (!state.hasCachedRead) {
    return true;
  }
  // a document actually changed, so re-read soon rather than on the retry
  // interval — but coalesce, because documents arrive in BATCHES. The first sync
  // of the 1584 curated-exercise documents delivers many batches, and without a
  // window each one would make the next exercise read re-scan the whole catalog.
  if (state.isStale) {
    return state.msSinceLastRead >= intervals.staleCoalesceMs;
  }
  if (state.lastReadWasUsable) {
    return false;
  }
  return state.msSinceLastRead >= intervals.retryIntervalMs;
}

/** What writing one curated-exercise text field back to the database requires. */
export enum ExerciseTextWriteAction {
  /** the stored value already says what it should — write nothing */
  None = 'None',
  /** no document exists yet and there is text to store */
  Create = 'Create',
  /** a document exists and its text must change (possibly to blank) */
  Update = 'Update',
}

/**
 * Decide what the admin save has to do with one `$text/en-us/@exercise:` field.
 *
 * Blanking a field is expressed as an UPDATE to blank text, never as a document
 * deletion. Deleting looks tidier but is a trap: the deleted document stays in
 * the identity map marked `_deleted`, so re-typing the field in the same session
 * would build a fresh object, bypass the identity map, and silently lose the
 * text. A blank document costs a hundred bytes and cannot do that.
 *
 * A field that has no document AND no text needs nothing — which is why the 75
 * curated exercises with no shortname never grow an empty one.
 */
export function decideExerciseTextWrite(
  existingText: string | undefined,
  newValue: string,
): ExerciseTextWriteAction {
  if (existingText === undefined) {
    return newValue.trim() === '' ? ExerciseTextWriteAction.None : ExerciseTextWriteAction.Create;
  }
  return existingText === newValue ? ExerciseTextWriteAction.None : ExerciseTextWriteAction.Update;
}

/**
 * Read one keyed-text document in the active language, falling back to the
 * English one when that language has no sibling for it.
 *
 * The fallback is PER DOCUMENT, which is what lets a corpus be translated
 * incrementally: a language that covers 40 of 86 help texts shows 40 translated
 * and 46 English, never a blank.
 *
 * `buildId` turns a locale segment into a document id, so the same helper works
 * for help texts and for any other `$text/` key scheme.
 *
 * Watcher caveat: a document that does NOT exist is never registered with the
 * caller's `DocWatcher` (the wrapper registers only after a successful read).
 * So on the fallback path only the English document is watched — if the
 * translation replicates down later, an open page keeps showing English until
 * it is reloaded. Acceptable because a newly arriving translation is not urgent,
 * and the next navigation picks it up.
 */
export async function readWithEnglishFallbackAsync<T>(
  buildId: (segment: TextLocaleSegment) => string,
  readDocAsync: (id: string) => Promise<T>,
  segment: TextLocaleSegment = ACTIVE_TEXT_SEGMENT,
): Promise<T> {
  // if the user is on English there is nothing to fall back to
  if (segment === ENGLISH_TEXT_SEGMENT) {
    return readDocAsync(buildId(ENGLISH_TEXT_SEGMENT));
  }
  try {
    return await readDocAsync(buildId(segment));
  } catch (err) {
    // expected: a `not_found` rejection from the read above, because this
    // language has no sibling for this document yet. That is a normal state
    // while a corpus is only partly translated, so it is deliberately not
    // reported — the English fallback below is the correct, non-blank result
    // and there is nothing for a debugger to act on. The drift gate is what
    // makes an untranslated document visible. Anything else (transport error,
    // corrupt document) is NOT swallowed.
    if (!isPouchNotFound(err)) {
      throw err;
    }
  }
  // a document missing in English too is a real problem, so this read is
  // allowed to throw for the caller to handle
  return readDocAsync(buildId(ENGLISH_TEXT_SEGMENT));
}

/** The parts of a keyed-text document that a translation replaces. */
export interface LocalizableText {
  _id: string;
  title: string;
  text: string;
}

/**
 * Merge a locale's help documents over the English ones for list views.
 *
 * English drives the result, so the list is never SHORTER for a non-English
 * user (an untranslated entry still appears, in English) and a stray localized
 * document with no English counterpart cannot add a phantom entry.
 *
 * Only the DISPLAY TEXT (`title` and `text`) is taken from the translation. The
 * English document supplies everything else, which matters for three reasons:
 *
 *  - `_id` stays the English one, so anything keyed by it stays stable across
 *    languages. The Help page passes it as the "seen" key and as a telemetry
 *    property; keying those by a localized id would drop a user's read marks
 *    when they switch language and split one help topic into four values.
 *  - `_rev` stays the English one, so revision comparisons cannot drift between
 *    unrelated per-locale revision lineages.
 *  - flags such as `dontShowInHelpPage` stay authoritative. A generated
 *    translation that omitted the flag would otherwise expose a deliberately
 *    hidden document to non-English users only.
 */
export function mergeLocalizedWithEnglish<T extends LocalizableText>(
  localizedDocs: readonly T[],
  englishDocs: readonly T[],
  malformedDocIds?: string[],
): T[] {
  const localizedByHelpId = new Map<string, T>();
  for (const localized of localizedDocs) {
    const helpId = tryHelpIdFromDocId(localized._id);
    // if this id does not parse, skip it — one malformed document must not
    // take down the whole list, but do report it through the out-array
    if (helpId === undefined) {
      malformedDocIds?.push(localized._id);
      continue;
    }
    localizedByHelpId.set(helpId, localized);
  }
  return englishDocs.map(english => {
    const helpId = tryHelpIdFromDocId(english._id);
    // if the English id does not parse it cannot be matched, so keep it in the
    // list rather than dropping it, and report it
    if (helpId === undefined) {
      malformedDocIds?.push(english._id);
      return english;
    }
    const localized = localizedByHelpId.get(helpId);
    // if this document has no translation yet, show the English one
    if (!localized) {
      return english;
    }
    // NOTE: this is a shallow COPY, not the cached database document. Nothing
    // may save an entry obtained from a merged list.
    return { ...english, title: localized.title, text: localized.text };
  });
}

/**
 * How many documents each side of a list read returned. The caller uses it to
 * spot a localized read that came back empty — see `readHelpListWithEnglishFallbackAsync`.
 */
export interface HelpListReadCounts {
  englishCount: number;
  localizedCount: number;
}

/**
 * Read the whole help list in one language, filling any untranslated entry from
 * English. English readers do a single query; everyone else does two and merges.
 *
 * `readListForPrefixAsync` runs one id-prefix query, so the caller owns the
 * database and its watcher and this stays testable without either.
 *
 * `counts` is an optional out-parameter filled with how many documents each read
 * returned. An id-prefix query USED TO return an empty list rather than failing
 * when the database was unavailable, which made "no localized documents" and
 * "the read did not work" look identical from here; it now throws
 * `DatabaseUnavailableError`, and this function lets that propagate to the
 * caller. What the counts still answer is the other question: every help
 * document has a sibling in every language, so a read that genuinely RAN and
 * returned nothing means the documents never replicated. Only the caller knows
 * enough to report that, so this reports the facts and decides nothing.
 */
export async function readHelpListWithEnglishFallbackAsync<T extends LocalizableText>(
  readListForPrefixAsync: (prefix: string) => Promise<T[]>,
  segment: TextLocaleSegment = ACTIVE_TEXT_SEGMENT,
  malformedDocIds?: string[],
  counts?: HelpListReadCounts,
): Promise<T[]> {
  const english = await readListForPrefixAsync(helpDocIdPrefixForSegment(ENGLISH_TEXT_SEGMENT));
  // if the user is on English there is no second list to merge
  if (segment === ENGLISH_TEXT_SEGMENT) {
    if (counts) {
      counts.englishCount = english.length;
      counts.localizedCount = english.length;
    }
    return english;
  }
  const localized = await readListForPrefixAsync(helpDocIdPrefixForSegment(segment));
  if (counts) {
    counts.englishCount = english.length;
    counts.localizedCount = localized.length;
  }
  return mergeLocalizedWithEnglish(localized, english, malformedDocIds);
}
